// Map<definitionId, { [timing]: [handler, ...] }>. A timing bucket is an
// ARRAY because a single card can print more than one independently-usable
// ability line under the same timing keyword (e.g. hero-drua-l2 has two
// separate "Dungeon:" lines) - the array's order matches the corresponding
// abilityText[timing] array produced by scripts/buildCardData.mjs, so
// abilityIndex in a dispatched action always lines up with the right text.
//
// Handler signature: (state, ctx) -> newState, where ctx is at minimum
// { playerId, instanceId } plus whatever the timing needs (choices,
// hallRankIndex, monsterInstanceId, ...). Handlers never pause mid-resolution
// to ask a question - callers supply ctx.choices already resolved (see the
// plan's "choices" contract). Most handlers fall back to a sensible default
// target when a specific choice isn't supplied, so the engine stays usable
// even before the UI grows per-card choice prompts.
const registry = new Map();

export function register(definitionId, handlers) {
  if (registry.has(definitionId)) {
    throw new Error(`Duplicate effect registration for ${definitionId}`);
  }
  registry.set(definitionId, handlers);
}

export function getHandlers(definitionId) {
  return registry.get(definitionId) || null;
}

// Returns the callable (state, ctx) -> newState function directly - card
// files register { resolve: fn } wrapper objects (so a registration block
// reads naturally next to the ability text it implements), unwrapped here so
// every call site can just do handler(state, ctx).
export function getHandler(definitionId, timing, abilityIndex = 0) {
  const handlers = registry.get(definitionId);
  const bucket = handlers && handlers[timing];
  const entry = bucket ? bucket[abilityIndex] : undefined;
  return entry ? entry.resolve : undefined;
}

export function hasHandler(definitionId, timing, abilityIndex = 0) {
  return Boolean(getHandler(definitionId, timing, abilityIndex));
}

// Optional sibling of `resolve`: (state, ctx) -> [instanceId, ...], the full
// list of legal targets for abilities that accept a player-chosen
// choices.heroInstanceId/instanceId. Lets the UI show a real picker instead
// of silently taking whatever `resolve`'s own fallback would have picked.
// Only meaningful for village/dungeon/spoils abilities - those are the only
// timings triggered by an explicit player button click, so they're the only
// ones with a natural pause point to show a picker before dispatching.
export function getCandidatesFn(definitionId, timing, abilityIndex = 0) {
  const handlers = registry.get(definitionId);
  const bucket = handlers && handlers[timing];
  const entry = bucket ? bucket[abilityIndex] : undefined;
  return (entry && entry.getCandidates) || null;
}

// Optional sibling of `resolve`: (state, ctx) -> [{type, key, cost}, ...],
// the legal stacks for a "Buy/Place 1 <kind of card>" ability (see
// cards/buyOneCard.js, which supplies this for every such card at once).
// The stack counterpart to getCandidates above: that one returns INSTANCE ids
// to highlight in hand, these are stacks with no hand presence at all, so the
// dock needs a picker rather than inline highlighting - the same "candidates
// that don't live in hand" split LevelUpChoicePanel/DiscardPileChoicePanel
// already have.
//
// Matt: "The clan champion spoils ability didn't let me choose what weapon to
// buy. This seems like a widespread bug of the game choosing things for me."
// Those handlers had no way to tell a caller what the options even were;
// this is what lets the dock offer them.
export function getBuyableSourcesFn(definitionId, timing, abilityIndex = 0) {
  const handlers = registry.get(definitionId);
  const bucket = handlers && handlers[timing];
  const entry = bucket ? bucket[abilityIndex] : undefined;
  return (entry && entry.getBuyableSources) || null;
}

// Optional sibling of `resolve`: (state, ctx) -> boolean, for abilities whose
// precondition isn't "pick a target from a list" (that's getCandidates) but
// a bare yes/no check on current game state (e.g. "only if equipped with a
// polearm," "only if there is Darkness," "only if rank 1 is occupied"). Lets
// callers ask "could this actually be used right now?" without invoking
// `resolve` and catching its thrown error - used by PlayerDock.vue's
// abilitiesFor to stop listing an ability as "unused" (and warning about it
// at end-of-turn) when it was never actually usable this turn in the first
// place. Matt reported this directly: ended a dungeon turn right after
// winning a fight, got warned about 3 unused abilities that were never
// triggerable (Regulars with no polearm equipped).
export function getIsUsableFn(definitionId, timing, abilityIndex = 0) {
  const handlers = registry.get(definitionId);
  const bucket = handlers && handlers[timing];
  const entry = bucket ? bucket[abilityIndex] : undefined;
  return (entry && entry.isUsable) || null;
}
